import { useState, type FormEvent } from 'react';
import { useLocation, useNavigate, useRevalidator } from 'react-router-dom';
import { Button, Checkbox, Input, Label, Modal } from '@immo/ui-kit';
import {
  MCP_ACCESS_VALIDITY_DAYS,
  DEFAULT_MCP_ACCESS_VALIDITY_DAYS,
  IssueMcpAccessBodySchema,
  MAX_ACTIVE_MCP_ACCESS_PER_ADMIN,
  type McpAccessOptionalGrant,
  type McpAccessValidityDays,
  type IssueMcpAccessResponse,
} from '@immo/api-contract';
import { apiClient } from '@/lib/auth/api-client';
import { useTranslations } from '@/i18n/use-translations';
import { useCan } from '@/lib/auth/permissions';
import { CopyIcon } from '@/components/layout/icons';
import { FormField } from '@/components/form';

type Field = 'name' | 'validityDays';
type FormErrors = Readonly<Record<Field, string | null>>;

const NO_ERRORS: FormErrors = { name: null, validityDays: null };

const SKILL_READ_GRANT: McpAccessOptionalGrant = 'backoffice:product-skill:read';
const SKILL_MANAGE_GRANT: McpAccessOptionalGrant = 'backoffice:product-skill:manage';
const MEMORY_MANAGE_GRANT: McpAccessOptionalGrant = 'backoffice:property:memory-manage';
const DOCUMENT_UPDATE_GRANT: McpAccessOptionalGrant = 'backoffice:property:document-update';
// Opt-in grants that write, not read (backend: PLATFORM_CONFIGURATION ∪ TENANT_DATA_WRITE).
const WRITE_GRANTS: ReadonlyArray<McpAccessOptionalGrant> = [
  SKILL_MANAGE_GRANT,
  MEMORY_MANAGE_GRANT,
  DOCUMENT_UPDATE_GRANT,
];

/**
 * Mint dialog for a MCP backoffice credential (IMM-522.1).
 *
 * Two phases in one component, exactly like the API-key mint dialog: the form,
 * then the show-once panel. The secret is never held anywhere but this
 * component's state — closing the dialog drops it, and no endpoint can return
 * it again. The operator copies the assembled `claude mcp add` command rather
 * than the bare secret, so the credential is never pasted somewhere by hand.
 */
export function IssueMcpAccessPage() {
  const t = useTranslations('pages.admin.mcp-access.issue');
  const navigate = useNavigate();
  const location = useLocation();
  const revalidator = useRevalidator();
  const canReadSkills = useCan(SKILL_READ_GRANT);
  const canManageSkills = useCan(SKILL_MANAGE_GRANT);
  const canManageMemory = useCan(MEMORY_MANAGE_GRANT);
  const canUpdateDocuments = useCan(DOCUMENT_UPDATE_GRANT);

  const [name, setName] = useState('');
  const [validityDays, setValidityDays] = useState<McpAccessValidityDays>(DEFAULT_MCP_ACCESS_VALIDITY_DAYS);
  const [grants, setGrants] = useState<ReadonlySet<McpAccessOptionalGrant>>(
    () => new Set(canReadSkills ? [SKILL_READ_GRANT] : []),
  );
  const [errors, setErrors] = useState<FormErrors>(NO_ERRORS);
  const [genericError, setGenericError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [issued, setIssued] = useState<IssueMcpAccessResponse | null>(null);
  const [copied, setCopied] = useState(false);

  const close = () => {
    if (submitting) return;

    // Only a successful mint changed the list; revalidating otherwise would be a
    // pointless round-trip on every cancel.
    if (issued !== null) revalidator.revalidate();

    navigate({ pathname: '/admin/mcp-access', search: location.search });
  };

  const toggleGrant = (grant: McpAccessOptionalGrant, checked: boolean) => {
    setGrants((previous) => {
      const next = new Set(previous);

      if (checked) next.add(grant);
      else next.delete(grant);

      return next;
    });
  };

  // The edit tools also need the read grant, so ticking edit ticks read; unticking leaves read as it was.
  const toggleManage = (checked: boolean) => {
    setGrants((previous) => {
      const next = new Set(previous);

      if (checked) {
        next.add(SKILL_MANAGE_GRANT);
        next.add(SKILL_READ_GRANT);
      } else {
        next.delete(SKILL_MANAGE_GRANT);
      }

      return next;
    });
  };

  const clearError = (field: Field) => {
    setErrors((previous) => (previous[field] === null ? previous : { ...previous, [field]: null }));
  };

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setGenericError(null);

    const parsed = IssueMcpAccessBodySchema.safeParse({ name, validityDays, optionalGrants: [...grants] });

    if (!parsed.success) {
      const next: Record<Field, string | null> = { ...NO_ERRORS };

      for (const issue of parsed.error.issues) {
        const field = fieldFromPath(issue.path);

        if (field !== undefined && next[field] === null) {
          next[field] = t(`errors.${errorMessageKey(field, issue.code)}`);
        }
      }

      setErrors(next);

      return;
    }

    setSubmitting(true);

    try {
      const result = await apiClient.adminMcpAccess.issue({ body: parsed.data });

      if (result.ok) {
        setIssued(result.body);

        return;
      }

      const body = result.body as { error?: { code?: string } } | undefined;
      const code = body?.error?.code;

      if (code === 'identity.mcp-access.name-already-used') {
        setErrors({ ...NO_ERRORS, name: t('errors.name-already-used') });
      } else if (code === 'identity.mcp-access.credential-limit-reached') {
        setGenericError(t('errors.limit-reached', { max: MAX_ACTIVE_MCP_ACCESS_PER_ADMIN }));
      } else {
        setGenericError(t('errors.generic'));
      }
    } catch {
      setGenericError(t('errors.generic'));
    } finally {
      setSubmitting(false);
    }
  };

  const copyCommand = async () => {
    if (issued === null) return;

    try {
      await navigator.clipboard.writeText(issued.connectCommand);
      setCopied(true);
    } catch {
      // Clipboard access can be denied; the command stays visible for manual copy.
    }
  };

  if (issued !== null) {
    return (
      <Modal
        open
        onClose={close}
        size="md"
        title={t('secret-heading')}
        footer={
          <Button type="button" onClick={close}>
            {t('done-cta')}
          </Button>
        }
      >
        <div className="space-y-3">
          <p className="text-base-sm text-text-secondary">{t('secret-warning')}</p>
          <Label htmlFor="mcp-access-command">{t('command-label')}</Label>
          <div className="flex items-center gap-2">
            <Input
              id="mcp-access-command"
              readOnly
              value={issued.connectCommand}
              className="font-mono"
              aria-label={t('command-label')}
            />
            <Button type="button" variant="ghost" onClick={copyCommand}>
              <CopyIcon className="h-4 w-4" />
              {t('copy-cta')}
            </Button>
          </div>
          {copied && <p className="text-sm text-text-tertiary">{t('copied-confirmation')}</p>}
          <p className="text-sm text-text-tertiary">
            {t(WRITE_GRANTS.some((grant) => grants.has(grant)) ? 'footnote-write' : 'footnote')}
          </p>
        </div>
      </Modal>
    );
  }

  return (
    <Modal
      open
      onClose={close}
      dirty={name !== ''}
      size="md"
      title={t('title')}
      footer={
        <>
          <Button variant="ghost" type="button" onClick={close} disabled={submitting}>
            {t('cancel-cta')}
          </Button>
          <Button type="submit" form="issue-mcp-access-form" disabled={submitting}>
            {submitting ? t('submit-loading') : t('submit-cta')}
          </Button>
        </>
      }
    >
      <form id="issue-mcp-access-form" className="space-y-5" onSubmit={submit} noValidate>
        <FormField id="mcp-access-name" label={t('name-label')} error={errors.name}>
          {(errorId) => (
            <Input
              id="mcp-access-name"
              value={name}
              placeholder={t('name-placeholder')}
              invalid={errors.name !== null}
              aria-describedby={errorId}
              onChange={(event) => {
                setName(event.target.value);
                clearError('name');
              }}
            />
          )}
        </FormField>

        <FormField id="mcp-access-validity" label={t('validity-label')} error={errors.validityDays}>
          {(errorId) => (
            <div className="flex flex-col gap-2">
              <div className="flex gap-2" role="radiogroup" aria-describedby={errorId}>
                {MCP_ACCESS_VALIDITY_DAYS.map((days) => (
                  <Button
                    key={days}
                    type="button"
                    size="sm"
                    role="radio"
                    aria-checked={validityDays === days}
                    variant={validityDays === days ? 'primary' : 'ghost'}
                    onClick={() => {
                      setValidityDays(days);
                      clearError('validityDays');
                    }}
                  >
                    {t('validity-option', { days })}
                  </Button>
                ))}
              </div>
              <p className="text-sm text-text-tertiary">{t('validity-hint')}</p>
            </div>
          )}
        </FormField>

        {(canReadSkills || canManageMemory || canUpdateDocuments) && (
          <div role="group" aria-labelledby="mcp-access-grants-label" className="space-y-2">
            <Label id="mcp-access-grants-label">{t('grants-label')}</Label>
            {canReadSkills && (
              <>
                <label className="flex items-center gap-2 text-sm text-text-primary">
                  <Checkbox
                    id="mcp-access-grant-skill-read"
                    checked={grants.has(SKILL_READ_GRANT)}
                    onCheckedChange={(checked) => toggleGrant(SKILL_READ_GRANT, checked)}
                    disabled={grants.has(SKILL_MANAGE_GRANT)}
                    aria-describedby="mcp-access-grant-skill-read-hint"
                  />
                  {t('grant.skill-read.label')}
                </label>
                <p id="mcp-access-grant-skill-read-hint" className="text-sm text-text-tertiary">
                  {t('grant.skill-read.hint')}
                </p>
                {canManageSkills && (
                  <>
                    <label className="flex items-center gap-2 text-sm text-text-primary">
                      <Checkbox
                        id="mcp-access-grant-skill-manage"
                        checked={grants.has(SKILL_MANAGE_GRANT)}
                        onCheckedChange={toggleManage}
                        aria-describedby="mcp-access-grant-skill-manage-hint"
                      />
                      {t('grant.skill-manage.label')}
                    </label>
                    <p id="mcp-access-grant-skill-manage-hint" className="text-sm text-text-tertiary">
                      {t('grant.skill-manage.hint')}
                    </p>
                  </>
                )}
              </>
            )}
            {canManageMemory && (
              <>
                <label className="flex items-center gap-2 text-sm text-text-primary">
                  <Checkbox
                    id="mcp-access-grant-memory-manage"
                    checked={grants.has(MEMORY_MANAGE_GRANT)}
                    onCheckedChange={(checked) => toggleGrant(MEMORY_MANAGE_GRANT, checked)}
                    aria-describedby="mcp-access-grant-memory-manage-hint"
                  />
                  {t('grant.memory-manage.label')}
                </label>
                <p id="mcp-access-grant-memory-manage-hint" className="text-sm text-text-tertiary">
                  {t('grant.memory-manage.hint')}
                </p>
              </>
            )}
            {canUpdateDocuments && (
              <>
                <label className="flex items-center gap-2 text-sm text-text-primary">
                  <Checkbox
                    id="mcp-access-grant-document-update"
                    checked={grants.has(DOCUMENT_UPDATE_GRANT)}
                    onCheckedChange={(checked) => toggleGrant(DOCUMENT_UPDATE_GRANT, checked)}
                    aria-describedby="mcp-access-grant-document-update-hint"
                  />
                  {t('grant.document-update.label')}
                </label>
                <p id="mcp-access-grant-document-update-hint" className="text-sm text-text-tertiary">
                  {t('grant.document-update.hint')}
                </p>
              </>
            )}
          </div>
        )}

        {genericError !== null && <p className="text-base text-danger-600">{genericError}</p>}
      </form>
    </Modal>
  );
}

function fieldFromPath(path: ReadonlyArray<PropertyKey>): Field | undefined {
  const head = path[0];

  if (head === 'name') return 'name';

  if (head === 'validityDays') return 'validityDays';

  return undefined;
}

// Zod's own message is never surfaced — the operator sees the pinned German
// copy keyed by what actually went wrong.
function errorMessageKey(field: Field, code: string): string {
  if (field === 'validityDays') return 'validity-required';

  return code === 'too_big' ? 'name-too-long' : 'name-required';
}
